import { graphql } from 'react-relay';

import { transformPreFilteredExtensions } from '@atlassian/forge-ui/web-client/transformForgeExtensions';
import type { Extension } from '@atlassian/forge-ui/web-client/graphql/types';

import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import { FORGE_MODULE_PAGE_BANNER } from '../ForgeModuleType.ts';

import type {
	preloadForgeApps_FetchPreFilteredForgeAppsQuery$data,
	preloadForgeApps_FetchPreFilteredForgeAppsQuery$variables,
} from './__generated__/preloadForgeApps_FetchPreFilteredForgeAppsQuery.graphql.ts';

// Inlined from platform/packages/forge/forge-ui/src/utils/createAtlAttributionHeader.ts
// (not available as a public entry point of @atlassian/forge-ui)
const createAtlAttributionHeader = (workspaceAri?: string): string =>
	JSON.stringify({ atlWorkspaceAri: workspaceAri, service: 'forge-ui' });

type PreloadForgeAppsResult =
	| { hasErrors: false; extensions: Extension[] | null }
	| { hasErrors: true; errors: ReadonlyArray<Error> };

export const preloadForgeApps = async ({
	cloudId,
	locale,
	types,
	context,
	includeHidden,
}: preloadForgeApps_FetchPreFilteredForgeAppsQuery$variables): Promise<PreloadForgeAppsResult> => {
	const { data, errors } = await queryAgg<
		preloadForgeApps_FetchPreFilteredForgeAppsQuery$data,
		preloadForgeApps_FetchPreFilteredForgeAppsQuery$variables
	>({
		query: graphql`
			query preloadForgeApps_FetchPreFilteredForgeAppsQuery(
				$cloudId: ID!
				$context: ConfluenceExtensionRenderingContextInput
				$includeHidden: Boolean
				$locale: String
				$types: [String]
				$includeSandboxPolicies: Boolean = false
			) {
				confluence_forgeExtensionsByType(
					cloudId: $cloudId
					context: $context
					includeHidden: $includeHidden
					locale: $locale
					types: $types
				) {
					id
					appId
					appVersion
					appOwner
					environmentId
					environmentType
					environmentKey
					consentUrl
					migrationKey
					userAccess {
						enabled
						hasAccess
					}
					key
					properties {
						key
						value
					}
					egress {
						addresses
						category
						type
						inScopeEUD
					}
					sandboxPolicies @include(if: $includeSandboxPolicies)
					scopes
					license {
						active
						capabilitySet
						billingPeriod
						ccpEntitlementId
						ccpEntitlementSlug
						isEvaluation
						subscriptionEndDate
						trialEndDate
						type
					}
					type
					installationId
					remoteInstallationRegion
					hiddenBy
				}
			}
		`,
		// copied from platform/packages/forge/forge-ui/src/web-client/hooks/usePrefilteredExtensionList.ts:createUsePrefilteredExtensionListQueryOptions()
		variables: {
			cloudId,
			context,
			includeHidden: includeHidden ?? false,
			types,
			locale,
			...(types?.includes(FORGE_MODULE_PAGE_BANNER) ? { includeSandboxPolicies: true } : {}),
		},
		/**
		 * We don't want to set 'ignore' because that discards errors and we need to inspect
		 * them for SLI reporting. We also need 'all' so we don't discard data on errors:http://go/j/AUX-317
		 * https://www.apollographql.com/docs/react/data/error-handling/
		 */
		errorPolicy: 'all',
		context: {
			headers: {
				'atl-attribution': createAtlAttributionHeader(cloudId),
			},
			allowOnExternalPage: true,
		},
	});

	// Banner discovery is authoritative during hydration. A failed/partial query must
	// leave discovery unknown so the client can retry, rather than publish "no apps".
	if (
		types?.includes(FORGE_MODULE_PAGE_BANNER) &&
		(errors?.length || !Array.isArray(data?.confluence_forgeExtensionsByType))
	) {
		// Preserve query errors and their handled marks for the shared preload error handler.
		return {
			hasErrors: true,
			errors: errors?.length
				? errors
				: [new Error('Forge page banner discovery returned no extension list')],
		};
	}

	// The shared transformer normalizes readonly, nullable GraphQL data, but its input
	// is still typed as normalized Extension[]. Keep this boundary suppression until
	// the shared transformer accepts the raw GraphQL type.
	// @ts-expect-error - Raw Relay results do not match the shared transformer input type.
	const extensions = (data?.confluence_forgeExtensionsByType ?? []) as Extension[];
	return {
		hasErrors: false,
		extensions: extensions.length > 0 ? transformPreFilteredExtensions(extensions) : null,
	};
};
